import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/16/solid'
import { CheckIcon } from '@heroicons/react/20/solid'
import { cn } from "../../lib/utils"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    )}
    {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
        position === "popper" &&
        "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}>
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn("p-1", position === "popper" &&
          "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]")}>
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1 pl-2 pr-7 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}>
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props} />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}



function ListboxOpenListener({ open, onOpen }) {

  const onOpenRef = React.useRef(onOpen);
  onOpenRef.current = onOpen;

  React.useEffect(() => {
    if (open) {
      onOpenRef.current?.();
    }
  }, [open]);

  return null;
}

// Nuevo componente: ColumnSelect (basado en Headless UI, sin avatares, reutilizable)

function ColumnSelect({ label = null, options, value, onChange, onOpen = () => {}, placeholder = "Selecciona una columna", labelClassName = "block text-sm font-medium text-gray-700 mb-2", className = "", disabled = false }) {



  const selectedOption = options.find(opt => opt === value) || null;

  return (
    <div className={disabled ? "opacity-60 cursor-not-allowed" : ""}>
      {label && <label className={labelClassName}>{label}</label>}

      <Listbox value={selectedOption} onChange={onChange} disabled={disabled}>
        {({ open }) => (
          <>
            <ListboxOpenListener open={open} onOpen={onOpen} />
            <div className="relative">
          <ListboxButton
            className={cn("relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left shadow-sm border border-gray-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] sm:text-sm", disabled && "bg-gray-50 text-gray-400 cursor-not-allowed hover:shadow-none", className)}
          >
            <span className={`block truncate text-sm ${selectedOption ? 'text-gray-900' : 'text-gray-400 italic'}`}>{selectedOption || placeholder}</span>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronUpDownIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
            </div>
          </ListboxButton>

          <ListboxOptions
            anchor="bottom start"
            className="z-[9999] mt-1 max-h-60 w-[var(--button-width)] overflow-auto rounded-md bg-white py-0.5 text-sm shadow-lg border border-gray-200 sm:text-sm focus:outline-none [--anchor-gap:4px]"
          >
            {options.map((option) => (
              <ListboxOption key={option} value={option} as={React.Fragment}>
                {({ active, selected }) => (
                  <li
                    className={`relative cursor-default select-none py-2 pl-3 pr-9 flex items-center justify-between transition-colors ${(active && !selected) ? 'bg-gray-100' : ''
                      } ${selected
                        ? 'text-[#1CB3B3] font-semibold'
                        : 'text-gray-900'
                      }`}
                  >
                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                      {option}
                    </span>

                    <span className="flex items-center pr-3">
                      {(!selected && active) ? (
                        <svg className="h-5 w-5 text-[#1CB3B3]" viewBox="0 0 20 20" fill="none" aria-hidden>
                          <path d="M4.5 10.5L8.5 14.5L15.5 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : null}
                    </span>
                  </li>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
              </div>
            </>
          )}
        </Listbox>
    </div>
  );
}

export { ColumnSelect };
